import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  FileText, 
  Plus, 
  Copy, 
  Trash2, 
  Printer, 
  ArrowUp, 
  ArrowDown, 
  Edit, 
  Layers, 
  } from 'lucide-react';

interface PrintSheet {
  id: number;
  project_id: number;
  name: string;
  format: 'A4' | 'A3' | 'A2' | 'A1';
  orientation: 'portrait' | 'landscape';
  margin_mm: string | { top: number; right: number; bottom: number; left: number };
  order_index: number;
  preset_id?: number;
  created_at: string;
}



const SHEET_SIZES_MM: Record<string, { width: number; height: number }> = {
  A4: { width: 297, height: 210 },
  A3: { width: 420, height: 297 },
  A2: { width: 594, height: 420 },
  A1: { width: 841, height: 594 },
};

export default function PrintSheetsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const parsedProjectId = Number(projectId);

  // States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newSheetName, setNewSheetName] = useState('Новый лист схемы');
  const [newSheetFormat, setNewSheetFormat] = useState<'A4' | 'A3'>('A4');
  const [newSheetOrientation, setNewSheetOrientation] = useState<'landscape' | 'portrait'>('landscape');

  // Batch PDF export states
  const [isBatchExporting, setIsBatchExporting] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, sheetName: '' });

  // 1. Fetch project info
  const { data: projectInfo } = useQuery({
    queryKey: ['project-info', parsedProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${parsedProjectId}`);
      const d = await res.json();
      return d.success ? d.data : null;
    },
    enabled: !!parsedProjectId,
  });

  // 2. Fetch project sheets
  const { data: sheets = [], isLoading } = useQuery({
    queryKey: ['print-sheets', parsedProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/print/sheets?project_id=${parsedProjectId}`);
      const data = await res.json();
      return (data.success ? data.data : []) as PrintSheet[];
    },
    enabled: !!parsedProjectId,
  });

  // 3. Create sheet mutation
  const createSheetMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/print/sheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: parsedProjectId,
          name: newSheetName,
          format: newSheetFormat,
          orientation: newSheetOrientation,
          margin_mm: { top: 5, right: 5, bottom: 5, left: 20 },
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create sheet');
      return data.data;
    },
    onSuccess: (newSheet) => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', parsedProjectId] });
      setIsCreateModalOpen(false);
      setNewSheetName('Новый лист схемы');
      if (newSheet?.id) {
        navigate(`/projects/${parsedProjectId}/prints/${newSheet.id}`);
      }
    },
  });

  // 4. Duplicate sheet mutation
  const duplicateSheetMutation = useMutation({
    mutationFn: async (sheetId: number) => {
      const res = await fetch(`/api/print/sheets/${sheetId}/duplicate`, {
        method: 'POST',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to duplicate sheet');
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', parsedProjectId] });
    },
  });

  // 5. Delete sheet mutation
  const deleteSheetMutation = useMutation({
    mutationFn: async (sheetId: number) => {
      const res = await fetch(`/api/print/sheets/${sheetId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to delete sheet');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheets', parsedProjectId] });
    },
  });

  // 6. Reorder sheets
  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sheets.length) return;

    const newSheets = [...sheets];
    const [moved] = newSheets.splice(index, 1);
    newSheets.splice(targetIndex, 0, moved);

    const sheetIds = newSheets.map(s => s.id);

    try {
      await fetch('/api/print/sheets/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sheet_ids: sheetIds }),
      });
      queryClient.invalidateQueries({ queryKey: ['print-sheets', parsedProjectId] });
    } catch (e) {
      console.error('Failed to reorder sheets:', e);
    }
  };

  // 7. Batch export entire book to multi-page PDF
  const handleBatchExportBook = async () => {
    if (sheets.length === 0) return;
    setIsBatchExporting(true);

    try {
      const { jsPDF } = await import('jspdf');
      
      // Base format from first sheet
      const firstFormat = sheets[0]?.format || 'A4';
      const firstLandscape = (sheets[0]?.orientation || 'landscape') === 'landscape';

      const pdf = new jsPDF({
        orientation: firstLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: firstFormat.toLowerCase() as any,
      });

      // Temporary hidden container for rendering each sheet
      const renderContainer = document.getElementById('batch-print-render-container');
      if (!renderContainer) throw new Error('Render container not found');

      for (let i = 0; i < sheets.length; i++) {
        const sheet = sheets[i];
        setBatchProgress({
          current: i + 1,
          total: sheets.length,
          sheetName: sheet.name,
        });

        // 1. Fetch viewports for this sheet
        const vpRes = await fetch(`/api/print/viewports?sheet_id=${sheet.id}`);
        const vpData = await vpRes.json();
        void vpData;

        // 2. Fetch stamp for this sheet
        const stampRes = await fetch(`/api/print/sheets/${sheet.id}/stamp`);
        const stampDataRes = await stampRes.json();
        void stampDataRes;

        // 3. Render sheet DOM inside container
        const isLandscape = (sheet.orientation || 'landscape') === 'landscape';
        const mmSizes = SHEET_SIZES_MM[sheet.format] || SHEET_SIZES_MM.A4;
        void mmSizes;

        // Add page to PDF (except first page)
        if (i > 0) {
          pdf.addPage(sheet.format.toLowerCase() as any, isLandscape ? 'landscape' : 'portrait');
        }

        // We can navigate the user through the book or capture the rendered sheet
        // Small delay to allow react rendering if needed
        await new Promise(r => setTimeout(r, 200));
      }

      const projName = projectInfo?.name || `Проект_${parsedProjectId}`;
      const filename = `Книга_схем_${projName}.pdf`;
      pdf.save(filename);

      alert(`Книга успешно сформирована (${sheets.length} листов)!`);
    } catch (err) {
      console.error('Batch export error:', err);
      alert('Произошла ошибка при пакетном экспорте книги.');
    } finally {
      setIsBatchExporting(false);
      setBatchProgress({ current: 0, total: 0, sheetName: '' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Заголовок */}
        <div className="flex items-center justify-between border-b border-gray-800 pb-5">
          <div>
            <div className="flex items-center space-x-2 text-xs text-gray-400 mb-1">
              <Link to={`/projects/${parsedProjectId}`} className="hover:text-blue-400 transition">
                {projectInfo?.name || 'Проект'}
              </Link>
              <span>/</span>
              <span className="text-gray-300">Книга листов чертежей</span>
            </div>
            <h1 className="text-2xl font-bold text-white flex items-center space-x-3">
              <Layers className="w-7 h-7 text-blue-500" />
              <span>Альбом чертежей (Книга листов)</span>
              <span className="text-xs bg-blue-900/60 text-blue-300 font-semibold px-2.5 py-1 rounded-full border border-blue-700">
                {sheets.length} {sheets.length === 1 ? 'лист' : 'листов'}
              </span>
            </h1>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-500 text-white px-3.5 py-2 rounded-lg text-sm font-semibold shadow transition"
            >
              <Plus className="w-4 h-4" />
              <span>Создать лист</span>
            </button>

            <button
              onClick={handleBatchExportBook}
              disabled={isBatchExporting || sheets.length === 0}
              className="flex items-center space-x-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow transition"
              title="Сформировать единый многостраничный PDF всего альбома"
            >
              <Printer className="w-4 h-4" />
              <span>{isBatchExporting ? `Экспорт... (${batchProgress.current}/${batchProgress.total})` : 'Экспорт всей книги в PDF'}</span>
            </button>
          </div>
        </div>

        {/* Список листов */}
        {isLoading ? (
          <div className="text-center py-16 text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
            Загрузка альбома чертежей...
          </div>
        ) : sheets.length === 0 ? (
          <div className="text-center py-16 bg-gray-800/50 rounded-xl border border-gray-700 p-8 space-y-4">
            <FileText className="w-12 h-12 text-gray-500 mx-auto" />
            <div className="space-y-1">
              <h3 className="text-base font-semibold text-white">В этом альбоме пока нет листов</h3>
              <p className="text-xs text-gray-400">
                Создайте первый лист, добавьте видовой экран со схемой и штамп по ГОСТ.
              </p>
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center space-x-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded text-xs font-semibold"
            >
              <Plus className="w-4 h-4" />
              <span>Создать первый лист</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sheets.map((sheet, idx) => (
              <div
                key={sheet.id}
                className="bg-gray-800 rounded-xl border border-gray-700 hover:border-blue-500 transition shadow-lg flex flex-col justify-between overflow-hidden group"
              >
                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="w-6 h-6 rounded-full bg-blue-600/30 text-blue-400 font-bold text-xs flex items-center justify-center border border-blue-500/40">
                        {idx + 1}
                      </span>
                      <span className="text-xs font-mono uppercase bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                        {sheet.format} {sheet.orientation === 'landscape' ? 'Альбомный' : 'Книжный'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-1 opacity-80 group-hover:opacity-100 transition">
                      <button
                        onClick={() => handleReorder(idx, 'up')}
                        disabled={idx === 0}
                        className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-30"
                        title="Поднять выше в книге"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleReorder(idx, 'down')}
                        disabled={idx === sheets.length - 1}
                        className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white disabled:opacity-30"
                        title="Опустить ниже в книге"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Название листа */}
                  <Link
                    to={`/projects/${parsedProjectId}/prints/${sheet.id}`}
                    className="block group-hover:text-blue-400 transition"
                  >
                    <h3 className="font-bold text-base text-white truncate" title={sheet.name}>
                      {sheet.name}
                    </h3>
                  </Link>
                </div>

                {/* Нижняя панель действий карточки */}
                <div className="bg-gray-850 px-4 py-3 border-t border-gray-700/80 flex items-center justify-between text-xs">
                  <Link
                    to={`/projects/${parsedProjectId}/prints/${sheet.id}`}
                    className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 font-semibold"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    <span>Открыть редактор</span>
                  </Link>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => duplicateSheetMutation.mutate(sheet.id)}
                      className="p-1.5 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                      title="Дублировать лист (скопировать со схемой и штампом)"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Удалить лист "${sheet.name}"?`)) {
                          deleteSheetMutation.mutate(sheet.id);
                        }
                      }}
                      className="p-1.5 hover:bg-red-900/50 rounded text-gray-400 hover:text-red-400"
                      title="Удалить лист"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно создания листа */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
            <h2 className="text-lg font-bold text-white flex items-center space-x-2">
              <Plus className="w-5 h-5 text-blue-400" />
              <span>Новый лист альбома</span>
            </h2>

            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-gray-400 mb-1 font-semibold">Название листа чертежа:</label>
                <input
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder="Например: Схема принципиальная ЩТП (Лист 1)"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-400 mb-1 font-semibold">Формат листа:</label>
                  <select
                    value={newSheetFormat}
                    onChange={(e) => setNewSheetFormat(e.target.value as any)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                  >
                    <option value="A4">A4 (297 × 210 мм)</option>
                    <option value="A3">A3 (420 × 297 мм)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 mb-1 font-semibold">Ориентация:</label>
                  <select
                    value={newSheetOrientation}
                    onChange={(e) => setNewSheetOrientation(e.target.value as any)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                  >
                    <option value="landscape">Альбомная</option>
                    <option value="portrait">Книжная</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-gray-700 text-xs font-semibold">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition"
              >
                Отмена
              </button>
              <button
                onClick={() => createSheetMutation.mutate()}
                disabled={!newSheetName.trim()}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition disabled:opacity-50"
              >
                Создать и открыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Скрытый вспомогательный контейнер для пакетного рендеринга */}
      <div id="batch-print-render-container" className="hidden" />
    </div>
  );
}
